<template>
  <div
    v-if="showTokenExpiredMessage"
    class="notification-message"
    @click="showSettingZoom"
  >
    <BIcon
      class="mr-100"
      size="small"
      type="error"
    >
      error
    </BIcon>
    <div class="message">
      {{ tokenExpiredMessage }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { ZoomSetting } from '@/api/openapi';
import { ZoomSettingApiService } from '@/api/user/resources/zoom_setting';
import ZoomSettingStatuses from '@/const/zoom_setting_statuses';
import { key } from '@/store/index';

type TEmit = {
  'click:setting': [];
  'update:setting-error': [error: boolean];
};

const emit = defineEmits<TEmit>();
const i18n = useI18n();
const router = useRouter();
const store = useStore(key);

const zoomSetting = ref<ZoomSetting | null>(null);

const canOpenSetting = computed(() =>
  ['owner', 'bpo_guest'].includes(store.getters['user/currentUser']?.role),
);

const showTokenExpiredMessage = computed(() =>
  zoomSetting.value?.apiSetting?.connectStatus === ZoomSettingStatuses.TOKEN_EXPIRED,
);

const tokenExpiredMessage = computed(() =>
  i18n.t('zoomNotification.tokenExpiredMessage') + (canOpenSetting.value ? i18n.t('general.goSetting') : ''),
);

const fetchZoomSetting = async () => {
  try {
    const { data } = await new ZoomSettingApiService().getZoomSetting({
      errorHandlers: { disableDefaultErrorHandler: true },
    });
    zoomSetting.value = data.zoomSetting;
  } catch (e) {
    // NOTE: 通知用の取得のため、失敗しても画面操作は妨げない（表示しないだけ）。
    console.error('Failed to fetch zoom setting for notification', e);
  }
};

const handleVisibilityChange = async () => {
  // NOTE: Zoom Phone 未使用のクライアントでは再取得しない（不要なリクエストを避ける）。
  //       判定は前回の取得結果に依存するため、初回取得に失敗した場合や後から利用を ON にした場合はリロードまで再取得されない。
  //       通知が出ないだけで画面操作は妨げないため許容する。
  if (!zoomSetting.value?.useZoomPhone) return;
  if (document.visibilityState === 'visible') {
    await fetchZoomSetting();
  }
};

onMounted(async () => {
  document.addEventListener('visibilitychange', handleVisibilityChange);
  await fetchZoomSetting();
});

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

watch(showTokenExpiredMessage, (newValue) => {
  emit('update:setting-error', newValue);
}, { immediate: true });

const showSettingZoom = () => {
  if (!canOpenSetting.value) return;
  emit('click:setting');
  router.replace({
    name: 'Setting',
    params: { tab: 'zoom' },
  });
};
</script>

<style lang="scss" scoped>
  .notification-message {
    display: flex;
    transition: $transition-base;
    margin: 10px 18px;
    padding: 10px;
    cursor: pointer;
    color: $basecolor-error;
    background: rgba($basecolor-error, 0.1);
    border-radius: 4px;
  }

  .message {
    font-size: $fontsize-100;
  }
</style>
